import { useCallback, useMemo } from "react";
import {
  MAP_SETTINGS_DEFAULTS,
  MAP_SETTINGS_SCOPE,
  MapSettings,
  withinLimits,
} from "../models/mapSettings";
import { useUserState } from "@/hooks/userState/useUserState";

export interface MapSettingsState {
  settings: MapSettings;
  /** Changes some of the settings and leaves the rest alone. */
  update: (patch: Partial<MapSettings>) => void;
  /** Whether the reader's stored settings are known yet. */
  isFetched: boolean;
}

/**
 * The reader's own map settings, remembered between visits and between devices.
 *
 * Everything read out of here has been pulled back inside the range its control allows, because
 * a stored record outlives the code that wrote it.
 */
export function useMapSettings(): MapSettingsState {
  const { value: settings, setValue, isReady } = useUserState<MapSettings>(
    MAP_SETTINGS_SCOPE,
    MAP_SETTINGS_DEFAULTS,
  );

  const update = useCallback(
    (patch: Partial<MapSettings>) => setValue(current => ({ ...current, ...patch })),
    [setValue],
  );

  const limited = useMemo(() => withinLimits(settings), [settings]);

  return { settings: limited, update, isFetched: isReady };
}
